// Подготовка клона DOM легенды к печати на растре (DA-15019).
//
// Веб-легенда содержит интерактивные контролы (тоглы, радиокнопки, выбор месяца/
// года/числа, поиск), уместные на странице, но не на «бумаге». Здесь клон легенды
// (html2canvas onclone) приводится к печатному виду:
//   - тёмная подложка как на странице (иначе белый текст не виден на белом листе);
//   - блоки с атрибутом `data-export-omit` убираются явно (радиогруппы, выбор
//     месяца — управляющие контролы помечены прямо в компонентах);
//   - включённые тоглы → только значок + подпись (сам переключатель убираем);
//   - выключенные тоглы → строка убирается целиком;
//   - прочие контролы без значков (поиск, кнопки, select) → убираем по эвристике;
//   - опустевшие группы и висящие заголовки убираем.
//
// Подход: символы легенды остаются по умолчанию, в экспорт НЕ идёт только то, что
// помечено `data-export-omit` либо опознано как контрол. Явный флаг
// (`data-export-omit`) — предпочтительный способ исключения: он не зависит от
// вёрстки/MUI-классов. Размечать им управляющие примитивы (StandardRadioGroup,
// MonthSelector и т.п.), а не угадывать их здесь.
//
// CSS-модули дают классы вида `FilterSidebar_checkboxIconGrid__hash`, поэтому
// селекторы по подстроке класса (`[class*="..."]`).

// Фон легенды как на странице (.mainLeg = #2e353dcc) — полупрозрачный, чтобы
// плашка-оверлей легенды на карте совпадала с плашкой заголовка.
export const LEGEND_BG = 'rgba(46, 53, 61, 0.8)';

// Темы оформления подложки легенды при экспорте.
const LEGEND_THEMES = {
    dark: { bg: LEGEND_BG, text: null },
    light: { bg: 'rgba(255, 255, 255, 0.85)', text: '#1f2933' },
};

// Атрибут-флаг «не рендерить при экспорте» (проставляется в компонентах легенды).
export const EXPORT_OMIT_ATTR = 'data-export-omit';

const remove = el => el && el.parentNode && el.parentNode.removeChild(el);
// Иконка внутри контрола (стрелка Select, иконка кнопки) — НЕ условное обозначение.
const isControlIcon = ic => !!ic.closest(
    `button, select, [${EXPORT_OMIT_ATTR}], [class*="MuiInputBase"], [class*="MuiSelect"]`
);
// Значок легенды: svg/img/canvas, не принадлежащий контролу.
const hasIcon = el => !!el && Array.from(el.querySelectorAll('svg, img, canvas'))
    .some(ic => !isControlIcon(ic));
// Текст без учёта пробелов (для поиска опустевших блоков).
const textOf = el => (el.textContent || '').replace(/\s+/g, '');
// Цветная плашка (градиентные легенды задают цвет инлайн-стилем фона).
const hasSwatch = el => !!el && (
    /background/i.test(el.getAttribute('style') || '')
    || !!el.querySelector('[style*="background"]')
);
// Реальное содержимое условных: значок / плашка / подпись.
const hasLegendContent = el => hasIcon(el) || hasSwatch(el) || textOf(el).length > 0;
// Заголовок группы фильтров.
const isHeader = el => /legHead/.test(el.className || '');
// Подзаголовки внутри грида (пары «пустой значок | текст» после схлопывания в 2
// колонки), под которыми не осталось ни одного под-тогла — убираем. Под-тоглы
// (существующая/предлагаемая) идут сразу за подзаголовком и опознаются по подписи
// с префиксом «-». Если все под-тоглы выключены, следующая за подзаголовком строка
// — уже другой пункт (или конец грида), и одинокий подзаголовок повис бы в легенде.
const isRowContent = row => !!row && (hasIcon(row[0]) || hasSwatch(row[0]));
const isRowSubheader = row => !!row && !isRowContent(row) && !!row[1] && textOf(row[1]).length > 0;
const isRowDashChild = row => isRowContent(row) && !!row[1] && textOf(row[1]).charAt(0) === '-';
const removeEmptyGridSubheaders = grid => {
    const cells = Array.from(grid.children);
    const rows = [];
    for (let i = 0; i < cells.length; i += 2) {
        rows.push([ cells[i], cells[i + 1] ]);
    }
    rows.forEach((row, idx) => {
        if (isRowSubheader(row) && !isRowDashChild(rows[idx + 1])) {
            row.forEach(remove);
        }
    });
};
// Тоглы вида `checkboxIconGrid`: 3 колонки-сиблинга на пункт — значок, подпись, переключатель.
const processToggleGrids = root => {
    const grids = root.querySelectorAll('[class*="checkboxIconGrid"]');

    grids.forEach(grid => {
        const checkboxes = grid.querySelectorAll('input[type="checkbox"]');

        checkboxes.forEach(cb => {
            // Ячейка-переключатель = прямой потомок грида, содержащий чекбокс.
            let toggleCell = cb;
            while (toggleCell && toggleCell.parentElement !== grid) {
                toggleCell = toggleCell.parentElement;
            }
            if (!toggleCell || !cb.checked) {
                // Выключенный (или «бесхозный») тогл → убираем всю «строку» триплетом
                // (значок + подпись + переключатель). Включённые трогаем ниже —
                // вместе со всем столбцом переключателей.
                if (toggleCell) {
                    const label = toggleCell.previousElementSibling;
                    const icon = label ? label.previousElementSibling : null;
                    remove(toggleCell);
                    remove(label);
                    remove(icon);
                }
            }
        });

        // Грид — строгие тройки ячеек (значок | подпись | переключатель), включая
        // строки-подзаголовки (пустой span | текст | пустой span). После удаления
        // выключенных строк оставшихся ячеек по-прежнему кратно 3. Убираем каждую
        // 3-ю ячейку — весь столбец переключателей — и схлопываем грид в честные
        // 2 колонки: выравнивание становится таким же ровным, как на странице, без
        // съезжающего потока ячеек.
        const cells = Array.from(grid.children);
        for (let i = 2; i < cells.length; i += 3) {
            remove(cells[i]);
        }
        grid.style.gridTemplateColumns = 'auto 1fr';
        // Высоту строки на странице задавала ячейка-переключатель (.switch ≈ 17px +
        // 5px паддинга сверху/снизу). Убрав её, строки сжимаются к высоте значка,
        // и пункты слипаются по вертикали. Возвращаем прежний воздух явным row-gap.
        grid.style.rowGap = '8px';

        // Убираем осиротевшие подзаголовки (все под-тоглы выключены).
        removeEmptyGridSubheaders(grid);

        // Группа без условных обозначений опустела — убираем её и предшествующий
        // заголовок. Условным считается и значок (svg/img/canvas), и цветная плашка
        // (свотч категории задаёт цвет инлайн-фоном span'а) — иначе легенды-свотчи
        // без иконок (напр. категории «ДТП с самокатами») стирались бы целиком.
        if (!hasIcon(grid) && !hasSwatch(grid)) {
            const head = grid.previousElementSibling;
            remove(grid);
            if (head && /legHead/.test(head.className || '')) {
                remove(head);
            }
        }
    });
};
// Контролы без значков, неуместные в печати, не помеченные `data-export-omit`
// явно (поиск, кнопки, нативные select). Убираем сам контрол вместе с его
// «блоком» (подпись/обёртка), но не задеваем значковые элементы легенды.
// Радиогруппы и выбор месяца сюда НЕ входят — они помечены флагом в компонентах.
const CONTROL_SELECTOR = [
    'input[type="text"]',
    'input[type="search"]',
    'input[type="number"]',
    'select',
    'button',
].join(', ');
const removeControls = (root, legendRoot) => {
    root.querySelectorAll(CONTROL_SELECTOR).forEach(el => {
        if (!el.isConnected) {
            return;
        }

        // Если контрол обёрнут в MUI-форму/нативную форму — убираем обёртку целиком.
        const wrapper = el.closest('[class*="MuiFormControl"], [class*="MuiTextField"], form');
        if (wrapper && legendRoot.contains(wrapper) && wrapper !== legendRoot) {
            remove(wrapper);
            return;
        }

        // Иначе убираем родительский блок, если он не несёт значков легенды
        // (заодно уносит подпись контрола, напр. «Выберите месяц»).
        const parent = el.parentElement;
        if (parent && parent !== legendRoot && !hasIcon(parent)) {
            remove(parent);
            return;
        }

        remove(el);
    });
};
// Финальная чистка: опустевшие блоки (без значка, без картинки, без текста).
const removeEmptyBlocks = (root, legendRoot) => {
    Array.from(root.querySelectorAll('div, section, ul, li')).forEach(el => {
        if (el === legendRoot || !el.isConnected) {
            return;
        }
        if (!textOf(el) && !hasIcon(el) && !hasSwatch(el)) {
            remove(el);
        }
    });
};
// Один проход: убирает заголовки без условных после них. Возвращает число удалённых.
const removeEmptyHeadersOnce = root => {
    let removed = 0;
    root.querySelectorAll('[class*="legHead"]').forEach(head => {
        if (!head.isConnected) {
            return;
        }

        let hasContent = false;
        let sib = head.nextElementSibling;
        while (sib && !isHeader(sib)) {
            // Сиблинг с собственным заголовком внутри — уже другая титулованная
            // секция (напр. обёртка LegendRenderer), её контент не наш.
            if (sib.querySelector('[class*="legHead"]')) {
                break;
            }
            if (hasLegendContent(sib)) {
                hasContent = true;
                break;
            }
            sib = sib.nextElementSibling;
        }

        if (!hasContent) {
            remove(head);
            removed += 1;
        }
    });
    return removed;
};
// Заголовки групп, после которых (до следующего заголовка) не осталось ни одного
// условного обозначения — убираем. Повторяем до стабилизации (вложенные секции).
const removeEmptyHeaders = root => {
    while (removeEmptyHeadersOnce(root) > 0) {
        // повторяем, пока есть что удалять
    }
};
// Инверсия цвета текста легенды для светлой темы. Цвет задаётся десятками правил
// в FilterSidebar.module.css (#fff / светло-серые), поэтому перекрываем всё разом
// одним инжектированным `!important`-правилом, scoped на #legend-grid. Затрагивает
// только `color` (текст и моно-иконки на `fill: currentColor`) — цветные плашки и
// SVG с явным `fill`/`background` (цвета слоёв) не меняются.
const applyTextTheme = (legendRoot, theme, clonedDoc) => {
    const colors = LEGEND_THEMES[theme] || LEGEND_THEMES.dark;
    if (!colors.text || !clonedDoc) {
        return;
    }

    const style = clonedDoc.createElement('style');
    style.textContent = `#legend-grid, #legend-grid * { color: ${colors.text} !important; }`;
    (clonedDoc.head || clonedDoc.body || legendRoot).appendChild(style);
};
// Раскладка легенды в несколько колонок, когда условные не помещаются в отведённую
// по вертикали высоту листа (maxHeightPx — в CSS-пикселях клона). Flex-контейнер
// с колоночным переносом: прямые потомки #legend-grid — это цельные группы условных
// одного слоя (HOC не добавляет обёрток), а flex-элементы не разрываются внутри —
// поэтому группа, не влезающая в остаток колонки, целиком уходит в следующую.
// Если всё помещается — остаётся одна колонка (перенос не срабатывает).
const applyColumnLayout = (legendRoot, maxHeightPx) => {
    if (!maxHeightPx || maxHeightPx <= 0) {
        return;
    }

    // Меряем натуральную высоту легенды (пока одна колонка, ещё исходный grid;
    // scrollHeight валиден — клон уже в layout внутри iframe html2canvas). Влезает
    // в доступную высоту листа — ничего не трогаем, экспорт как прежде.
    const limit = Math.floor(maxHeightPx);
    if (legendRoot.scrollHeight <= limit) {
        return;
    }

    // Не влезает — раскладываем условные по колонкам. Flex с колоночным переносом:
    // прямые потомки #legend-grid — цельные группы условных одного слоя (HOC не
    // добавляет обёрток), а flex-элементы не разрываются внутри, поэтому не
    // влезающая в остаток колонки группа уходит в следующую целиком.
    legendRoot.style.display = 'flex';
    legendRoot.style.flexDirection = 'column';
    legendRoot.style.flexWrap = 'wrap';
    legendRoot.style.alignContent = 'flex-start';
    legendRoot.style.alignItems = 'flex-start';
    legendRoot.style.boxSizing = 'border-box';
    legendRoot.style.rowGap = '5px';      // как grid-gap исходной легенды
    legendRoot.style.columnGap = '18px';  // зазор между колонками

    // Колоночный перенос во flex-direction: column включает ТОЛЬКО явная высота
    // (max-height не запускает — контейнер просто растёт в одну колонку). Фиксируем
    // height = доступной высоте: браузер сам раскидывает группы по нужному числу
    // колонок.
    legendRoot.style.height = `${limit}px`;

    // #legend-grid — flex-элемент .mainLeg и по умолчанию растягивается на её
    // ширину (~ширина сайдбара). Из-за этого его border-box остаётся узким, вторая
    // колонка выходит за него и не попадает в растр. Отвязываем ширину от родителя:
    // max-content под фактическое число колонок, без сжатия/растяжения.
    legendRoot.style.width = 'max-content';
    legendRoot.style.maxWidth = 'none';
    legendRoot.style.alignSelf = 'flex-start';
    legendRoot.style.flexShrink = '0';

    // Группы не растягиваем на всю колонку и не даём разрываться при переносе.
    Array.from(legendRoot.children).forEach(child => {
        child.style.flex = '0 0 auto';
        child.style.breakInside = 'avoid';
    });
};

/**
 * Готовит клонированный узел легенды к печати. Возвращает тот же узел (мутирует).
 * @param {HTMLElement} legendRoot клон контейнера #legend-grid
 * @param {Object}      [opts]
 * @param {string}      [opts.theme]       'dark' | 'light'
 * @param {Document}    [opts.clonedDoc]   клон документа (для инжекта стилей темы)
 * @param {number}      [opts.maxHeightPx] доступная высота легенды (CSS-px клона);
 *                                         при нехватке — раскладка в несколько колонок
 */
export const prepareLegendClone = (legendRoot, { theme = 'dark', clonedDoc = null, maxHeightPx = null } = {}) => {
    if (!legendRoot) {
        return legendRoot;
    }

    // Явно помеченные контролы (радиогруппы, выбор месяца и т.п.) — убираем сразу.
    legendRoot.querySelectorAll(`[${EXPORT_OMIT_ATTR}]`).forEach(remove);

    processToggleGrids(legendRoot);
    removeControls(legendRoot, legendRoot);
    removeEmptyBlocks(legendRoot, legendRoot);
    removeEmptyHeaders(legendRoot);

    // Подложка по теме + аккуратные отступы карточки.
    const themed = LEGEND_THEMES[theme] || LEGEND_THEMES.dark;
    legendRoot.style.background = themed.bg;
    legendRoot.style.padding = '12px';
    legendRoot.style.borderRadius = '8px';
    applyTextTheme(legendRoot, theme, clonedDoc);
    applyColumnLayout(legendRoot, maxHeightPx);

    return legendRoot;
};
